import { apiAIService } from "../services/api";
import { dedupe } from "./requestDedup";

// API configuration and utility functions
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.MODE === "production"
    ? "https://api.finu.com"
    : "http://localhost:8000");

export const apiClient = {
  async get(endpoint) {
    const token = localStorage.getItem("token");
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.statusText}`);
    }

    return response.json();
  },

  async post(endpoint, data) {
    const token = localStorage.getItem("token");
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.statusText}`);
    }

    return response.json();
  },

  async put(endpoint, data) {
    const token = localStorage.getItem("token");
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.statusText}`);
    }

    return response.json();
  },

  async delete(endpoint) {
    const token = localStorage.getItem("token");
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.statusText}`);
    }

    return response.json();
  },
};

// Authentication endpoints
export const authAPI = {
  googleLogin: (token) => apiClient.post("/auth/google", { token }),
  web3Login: (walletAddress, jwt) =>
    apiClient.post("/auth/web3", { walletAddress, jwt }),
  logout: () => apiClient.post("/auth/logout", {}),
  refreshToken: () => apiClient.post("/auth/refresh", {}),
  updateAskFinuOnboarding: (data) =>
    apiAIService.post("/auth/ask-finu-onboarding", data),
};

// Financial data endpoints
export const financialAPI = {
  getDashboardAnalytics: (period = "7_days", timeUnit = "days") =>
    apiClient.get(
      `/dashboard/analytics?period=${period}&time_unit=${timeUnit}`,
    ),
  getDashboardAiInsights: () => apiAIService.get("/dashboard/ai-insights"),
  getTransactions: (params) =>
    apiClient.get(`/transactions?${new URLSearchParams(params)}`),
  addTransaction: (transaction) => apiClient.post("/transactions", transaction),
  getBudget: () => apiClient.get("/budget"),
  updateBudget: (budget) => apiClient.put("/budget", budget),
  getInsights: () => apiClient.get("/insights"),
  connectBank: (plaidToken) => apiClient.post("/bank/connect", { plaidToken }),
};

// Goals endpoints
export const goalsAPI = {
  getGoals: () => apiAIService.get("/goals"),
  getGoal: (goalId) => apiAIService.get(`/goals/${goalId}`),
  createGoal: (goalData) => apiAIService.post("/goals", goalData),
  updateGoal: (goalId, goalData) =>
    apiAIService.put(`/goals/${goalId}`, goalData),
  deleteGoal: (goalId) => apiAIService.delete(`/goals/${goalId}`),
  getGoalsProgress: () => apiAIService.get("/goals/progress/summary"),
  addFundsToGoal: (goalId, transactionData) =>
    apiAIService.post(`/goals/${goalId}/add-funds`, transactionData),
  withdrawFundsFromGoal: (goalId, transactionData) =>
    apiAIService.post(`/goals/${goalId}/withdraw-funds`, transactionData),
  getGoalTransactions: (goalId, params = {}) =>
    apiAIService.get(
      `/goals/${goalId}/transactions?${new URLSearchParams(params)}`,
    ),
  getGoalTransactionSummary: (goalId) =>
    apiAIService.get(`/goals/${goalId}/transaction-summary`),
};

// Chat endpoints
export const chatAPI = {
  sendMessage: (message, conversationId = null) =>
    apiAIService.post("/chat/financial-advice", {
      message,
      conversation_id: conversationId,
    }),
  sendMessageStream: (message, conversationId = null) =>
    apiAIService.post("/chat/financial-advice-stream", {
      message,
      conversation_id: conversationId,
    }),
  getConversations: () => apiAIService.get("/chat/conversations"),
  getConversation: (conversationId) =>
    apiAIService.get(`/chat/conversation/${conversationId}`),
  startNewConversation: () => apiAIService.post("/chat/start-new-conversation"),
  switchConversation: (conversationId) =>
    apiAIService.post(`/chat/switch-conversation/${conversationId}`),
  deleteConversation: (conversationId) =>
    apiAIService.delete(`/chat/conversation/${conversationId}`),
  renameConversation: (conversationId, newTitle) =>
    apiAIService.put(`/chat/conversation/${conversationId}/rename`, {
      new_title: newTitle,
    }),
  getChatHistory: (params = {}) =>
    apiAIService.get(`/chat/history?${new URLSearchParams(params)}`),
  clearMemory: (conversationOnly = false) =>
    apiAIService.delete(
      `/chat/clear-memory?conversation_only=${conversationOnly}`,
    ),
  getChatStatistics: () => apiAIService.get("/chat/statistics"),
};

// AI endpoints
export const aiAPI = {
  sendMessage: (message, context) =>
    apiAIService.post("/ai/chat", { message, context }),
  getRecommendations: () => apiAIService.get("/ai/recommendations"),
  analyzeSpending: (data) => apiAIService.post("/ai/analyze", data),
};

// Token endpoints
export const tokenAPI = {
  getBalance: () => apiAIService.get("/tokens/balance"),
  getEarnings: () => apiAIService.get("/tokens/earnings"),
  claimRewards: () => apiAIService.post("/tokens/claim", {}),
  getTransactions: () => apiAIService.get("/tokens/transactions"),
};

// Subscription endpoints
export const subscriptionAPI = {
  getPlans: () => apiAIService.get("/subscription/plans"),
  subscribe: (planId) =>
    apiAIService.post("/subscription/create-checkout-session", {
      plan_id: planId,
    }),
  cancelSubscription: () => apiAIService.post("/subscription/cancel", {}),
  getSubscriptionStatus: () =>
    dedupe("util:subscription:status", () =>
      apiAIService.get("/subscription/status"),
    ),
  openBillingPortal: () => apiAIService.post("/subscription/portal", {}),
  startTrial: () => apiAIService.post("/subscription/trial/start", {}),
  endTrialNow: () => apiAIService.post("/subscription/trial/end-now", {}),
  confirmCheckout: (sessionId) =>
    apiAIService.post("/subscription/confirm", { session_id: sessionId }),
};
